import { defineMessages } from 'react-intl.macro';
import React, { useMemo, useCallback, useState, SyntheticEvent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { CancelTokenSource } from 'axios';

import { FormatMessage, useMessage } from '@tmapy/intl';
import { useSelector, useDispatch, Dispatch } from '@tmapy/redux';

import { SvgTrash, SvgCross, SvgSpinner, BtnBar, BtnBarSpan, CtaBtn, DangerAlert, Icon, Link, SecondaryBtn, SecondaryLink, TertiaryBtn, Table, UploadDropZone, WaitingAlert } from '@tmapy/style-guide';

import type { DocRetrieveResponse, NestedAttachmentResponse } from '../../openapi/types';

import type { ServerErrorItem } from '../../hooks/useFormItemServerErrors';
import type { UseAuth } from '../../hooks/useAuth';
import { useAuth, useDownloadLink, useFormItemServerErrors } from '../../hooks';
import { UploadBtnWithDropZone } from '../../app/components/UploadBtnWithDropZone';
import { callApi } from '../../utils/api';
import { QUERY_STATE } from '../../constants';
import { commonMessages } from '../../commonMessages';

import { uploadFiles, deleteFile, cancelUploadFile, loadDoc } from '../actions/docDetail';
import { ATTACHMENT_TYPES, DOC_STATE } from '../constants';

import { attachmentTypesMessages } from './docsMessages';
import { AttachmentTypeSelect } from './AttachmentTypeSelect';
import { FileIconWithBadge } from './FileIconWithBadge';

const msg = defineMessages({
  fileName: {
    id: 'docAttachments.fileName',
    defaultMessage: 'Název souboru',
  },
  fileType: {
    id: 'docAttachments.fileType',
    defaultMessage: 'Typ dokumentu',
  },
  fileDate: {
    id: 'docAttachments.fileDate',
    defaultMessage: 'Datum nahrání',
  },
  fileSize: {
    id: 'docAttachments.fileSize',
    defaultMessage: 'Velikost souboru',
  },
  deleteFileBtn: {
    id: 'docAttachments.deleteFileBtn',
    defaultMessage: 'Smazat',
  },
  cancelUploadBtn: {
    id: 'docAttachments.cancelUploadBtn',
    defaultMessage: 'Zrušit',
  },
  uploading: {
    id: 'docAttachments.uploading',
    defaultMessage: 'Nahrávání...',
  },
  uploadDropZone: {
    id: 'docAttachments.uploadDropZone',
    defaultMessage: 'Sem přetáhněte vaše soubory',
  },
  uploadBtnText: {
    id: 'docAttachments.uploadBtnText',
    defaultMessage: 'Sem přetáhněte vaše soubory nebo',
  },
  uploadBtn: {
    id: 'docAttachments.uploadBtn',
    defaultMessage: 'Vyberte soubory',
  },
  deleteAllBtn: {
    id: 'docAttachments.deleteAllBtn',
    defaultMessage: 'Smazat vše',
  },
  deleteFileAlert: {
    id: 'docAttachments.deleteFileAlert',
    defaultMessage: 'Opravdu chcete smazat přílohu?',
  },
  deleteAllAlert: {
    id: 'docAttachments.deleteAllAlert',
    defaultMessage: 'Opravdu chcete smazat všechny přílohy?',
  },
  downloadAllBtn: {
    id: 'docAttachments.downloadAllBtn',
    defaultMessage: 'Stáhnout vše',
  },
  importFailedAlert: {
    id: 'docAttachments.importFailedAlert',
    defaultMessage: 'Přílohy se nezdařilo naimportovat: {statusText}',
  },
  importWaitingAlert: {
    id: 'docAttachments.importWaitingAlert',
    defaultMessage: 'Čekejte prosím...',
  },
  checkBtn: {
    id: 'docAttachments.checkBtn',
    defaultMessage: 'Zkontrolovat',
  },
});

export type AttachmentId = NestedAttachmentResponse['url'];

export type AttachmentChangeData = {
  attachmentId: AttachmentId;
};

type DocWithAttachments = Partial<
  Pick<
    DocRetrieveResponse,
    'state' | 'flow' | 'id' | 'url' | 'attachments' | 'download_attachments_link'
  >
>;

type DocAttachmentsProps = {
  doc?: DocWithAttachments | null;
  prejimkaType?: DocRetrieveResponse['prejimka_type'];
  attachments: (NestedAttachmentResponse & { deleteQueryState?: QUERY_STATE })[];
  attachmentTypes?: typeof ATTACHMENT_TYPES;
  defaultAttachmentType?: ATTACHMENT_TYPES;
  createDoc?: () => Promise<DocWithAttachments | null>;
  importError?: ServerErrorItem;
  importQueryState?: QUERY_STATE;
  onCheckBtnClick?: () => void;
  isCheckBtnDisabled?: boolean;
  isDisabled?: boolean;
};

type Acl = {
  canDestroy: boolean;
  canUpload: boolean;
};

export const DocAttachments: React.FC<DocAttachmentsProps> = ({
  doc: initialDoc,
  prejimkaType,
  attachments,
  attachmentTypes,
  defaultAttachmentType,
  createDoc,
  importError,
  importQueryState,
  onCheckBtnClick,
  isCheckBtnDisabled,
  isDisabled: isDisabledProp,
}) => {
  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const auth = useAuth();
  const uploadingAttachments = useSelector((state) => state.doc.uploadingAttachments);
  const uploadQueryState = useSelector((state) => state.doc.uploadQueryState);
  const uploadErrors = useSelector((state) => state.doc.uploadErrors);
  const formItemServerErrors = useFormItemServerErrors(uploadErrors);

  const [doc, setDoc] = useState(initialDoc);
  useEffect(() => {
    setDoc({
      ...doc,
      state: initialDoc?.state,
      flow: initialDoc?.flow,
    });
  }, [initialDoc]);

  const acl: Acl = useMemo(
    () => ({
      canDestroy: auth.hasPermission('Attachments', 'destroy', {
        state: doc?.state ?? DOC_STATE.DRAFT,
        flow: doc?.flow ?? '',
      }),
      canUpload: auth.hasPermission('Attachments', 'create', {
        state: doc?.state ?? DOC_STATE.DRAFT,
        flow: doc?.flow ?? '',
      }),
    }),
    [auth, doc?.state, doc?.flow],
  );

  const deleteAllFiles = useCallback(
    async (docUrl: string) => {
      if (!doc?.id) return;
      const response = await callApi(`${docUrl}delete_attachments/`, 'DELETE');
      if (response.data) {
        dispatch(loadDoc(doc?.id));
      }
    },
    [dispatch, doc?.id],
  );

  const handleUpload = async (event: SyntheticEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    if (!input.files?.length) return;
    if (doc?.url) {
      return dispatch(uploadFiles(input, doc?.url, defaultAttachmentType));
    } else if (createDoc) {
      const data = await createDoc();
      if (data?.url) {
        setDoc(data);
        return dispatch(uploadFiles(input, data.url, defaultAttachmentType));
      }
    }
  };

  const handleCancelBtnClick = (e: React.MouseEvent, eventData: { data: CancelTokenSource }) => {
    dispatch(cancelUploadFile(eventData.data));
  };

  const handleDeleteAllBtnClick = () => {
    if (!doc?.url) return;
    if (window.confirm(formatMessage(msg.deleteAllAlert))) {
      deleteAllFiles(doc.url);
    }
  };

  const hasImportStarted =
    importQueryState && [QUERY_STATE.WAITING, QUERY_STATE.SUCCESS].includes(importQueryState);
  const someAttachments = attachments?.length > 0 || uploadingAttachments?.length > 0;

  const isDisabled = hasImportStarted || !acl.canUpload || isDisabledProp;

  const downloadAllAttachmentsLink = useDownloadLink(
    doc?.url || '',
    doc?.flow || prejimkaType,
    'download_all_attachments',
  );

  return (
    <div className='sg-u-vs-2'>
      {uploadQueryState === QUERY_STATE.FAIL && formItemServerErrors.non_field_errors && (
        <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
      )}

      {uploadQueryState === QUERY_STATE.FAIL && formItemServerErrors.file && (
        <DangerAlert>{formItemServerErrors.file.errorMessage!}</DangerAlert>
      )}
      <UploadDropZone
        label={formatMessage(commonMessages.uploadDropZone)}
        btnLabel={formatMessage(commonMessages.uploadBtn)}
        isDisabled={isDisabled}
        isShown={!someAttachments}
        name='files'
        onDrop={handleUpload}
      >
        <div className='sg-a-d-f sg-a-fd-c sg-a-ai-s sg-u-vs-2'>
          {someAttachments && (
            <div className='sg-a-mx--2'>
              <Table>
                <thead>
                  <tr>
                    <th />
                    <th>
                      <FormattedMessage {...msg.fileName} />
                    </th>
                    <th>
                      <FormattedMessage {...msg.fileType} />
                    </th>
                    <th className='sg-a-ta-r'>
                      <FormattedMessage {...msg.fileDate} />
                    </th>
                    <th className='sg-a-ta-r'>
                      <FormattedMessage {...msg.fileSize} />
                    </th>
                    {(acl.canDestroy && !isDisabledProp) && <th />}
                  </tr>
                </thead>
                <tbody>
                  {attachments?.map((attachment) => {
                    return (
                      <AttachmentItem
                        isDisabled={isDisabledProp}
                        key={attachment.uuid ?? attachment.created}
                        dispatch={dispatch}
                        doc={doc}
                        acl={acl}
                        auth={auth}
                        attachment={attachment}
                        attachmentTypes={attachmentTypes}
                        formatMessage={formatMessage}
                        hasImportStarted={hasImportStarted}
                        prejimkaType={prejimkaType}
                      />
                    );
                  })}
                  {uploadingAttachments.map((attachment) => (
                    <tr key={attachment.name}>
                      <td>{attachment.name}</td>
                      <td colSpan={3}>
                        {attachment.progress && (
                          <WaitingAlert>
                            {`${formatMessage(msg.uploading)} ${Math.round(
                              attachment.progress * 100,
                            )}% `}
                          </WaitingAlert>
                        )}
                      </td>
                      <td className='sg-a-w-0'>
                        {attachment.cancelToken && (
                          <TertiaryBtn
                            icon={{ element: <SvgCross /> }}
                            isDanger
                            data={attachment.cancelToken}
                            tooltip={formatMessage(msg.cancelUploadBtn)}
                            onClick={handleCancelBtnClick}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {importError && (
            <DangerAlert>
              {<FormattedMessage {...msg.importFailedAlert} values={importError} />}
            </DangerAlert>
          )}

          {importQueryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.importWaitingAlert} />
            </WaitingAlert>
          )}

          <BtnBar>
            {someAttachments && onCheckBtnClick && (
              <CtaBtn onClick={onCheckBtnClick} isDisabled={isCheckBtnDisabled}>
                <FormattedMessage {...msg.checkBtn} />
              </CtaBtn>
            )}
            {(attachments?.length ?? 0) > 1 &&
              auth.hasPermission('Attachments', 'delete_attachments', {
                state: doc?.state ?? DOC_STATE.DRAFT,
                flow: doc?.flow ?? '',
              }) && (
                <SecondaryBtn isDanger onClick={handleDeleteAllBtnClick}>
                  <FormattedMessage {...msg.deleteAllBtn} />
                </SecondaryBtn>
              )}
            {someAttachments && downloadAllAttachmentsLink.href && (
              <SecondaryLink {...downloadAllAttachmentsLink} download>
                <FormattedMessage {...msg.downloadAllBtn} />
              </SecondaryLink>
            )}
            <BtnBarSpan />
            {someAttachments && acl.canUpload && (
              <UploadBtnWithDropZone
                label={formatMessage(msg.uploadBtnText)}
                btnLabel={formatMessage(msg.uploadBtn)}
                isDisabled={hasImportStarted}
                onChange={handleUpload}
              />
            )}
          </BtnBar>
        </div>
      </UploadDropZone>
    </div>
  );
};

type AttachmentItemProps = {
  dispatch: Dispatch;
  doc?: DocWithAttachments | null;
  prejimkaType?: DocRetrieveResponse['prejimka_type'];
  acl: Acl;
  auth: UseAuth;
  attachment: NestedAttachmentResponse & { deleteQueryState?: QUERY_STATE };
  attachmentTypes?: typeof ATTACHMENT_TYPES;
  formatMessage: FormatMessage;
  hasImportStarted: boolean | undefined;
  isDisabled?: boolean;
};
export const AttachmentItem: React.FC<AttachmentItemProps> = ({
  dispatch,
  doc,
  prejimkaType,
  acl,
  auth,
  attachment,
  attachmentTypes,
  formatMessage,
  hasImportStarted,
  isDisabled,
}) => {
  const isDeleting = attachment.deleteQueryState === QUERY_STATE.WAITING;
  const areActionsDisabled = hasImportStarted || isDeleting;

  const canUpdateAttachments = auth.hasPermission('Attachments', 'partial_update', {
    state: doc?.state ?? DOC_STATE.DRAFT,
    flow: doc?.flow ?? '',
  });

  const handleDeleteBtnClick = (e: React.MouseEvent, eventData: { data: string }) => {
    if (window.confirm(formatMessage(msg.deleteFileAlert))) {
      dispatch(deleteFile(eventData.data));
    }
  };

  const attachmentTypeItems = useMemo(() => {
    return (Object.keys(attachmentTypes || ATTACHMENT_TYPES) as ATTACHMENT_TYPES[]).map(
      (value) => ({
        value,
        label: formatMessage.lookup(attachmentTypesMessages, ATTACHMENT_TYPES[value])!,
      }),
    );
  }, [formatMessage, attachmentTypes]);

  const downloadUrl = `${doc?.url}attachments/${attachment.id}/`;
  const downloadAttachmentLink = useDownloadLink(
    downloadUrl,
    doc?.flow || prejimkaType,
    'download_attachment',
  );

  return (
    <tr key={attachment.url}>
      <td className='sg-a-w-0'>
        <FileIconWithBadge name={attachment.name} />
      </td>
      <td>
        <Link {...downloadAttachmentLink} severity='colorful' download={attachment.name}>
          <div className='sg-a-ow-a'>{attachment.name}</div>
        </Link>
      </td>
      <td>
        {canUpdateAttachments ? (
          <AttachmentTypeSelect
            defaultValue={attachment.attachment_type}
            url={attachment.url}
            isDisabled={areActionsDisabled}
            items={attachmentTypeItems}
          />
        ) : (
          formatMessage.lookup(
            attachmentTypesMessages,
            ATTACHMENT_TYPES[attachment.attachment_type],
          )
        )}
      </td>
      <td className='sg-a-ta-r'>
        {attachment.created && new Date(attachment.created).toLocaleString('cs-CZ')}
      </td>
      <td className='sg-a-ta-r'>{attachment.size}</td>
      {(acl.canDestroy && !isDisabled) && (
        <td className='sg-a-w-0'>
          {!isDeleting ? (
            <TertiaryBtn
              icon={{ element: <SvgTrash /> }}
              isDisabled={areActionsDisabled}
              isDanger
              data={attachment.url}
              tooltip={formatMessage(msg.deleteFileBtn)}
              onClick={handleDeleteBtnClick}
            />
          ) : (
            <Icon element={<SvgSpinner />} size="med" />
          )}
        </td>
      )}
    </tr>
  );
};
